"use client";

import { BrokerProfileCard } from "@/components/brokers/broker-profile-card";
import { api } from "@/lib/api-client";
import type { Agent } from "@/lib/types";
import { buildAgentProfileUrl } from "@/lib/url";
import { displayBrokerageName } from "@repo/utils";
import { differenceInYears } from "date-fns";
import { useEffect, useMemo, useState } from "react";

export type AgentListItemProps = {
  agent: Agent;
};

function extractCityState(agent: Agent): string | null {
  const areas = agent.agentServiceAreas;
  if (!areas || areas.length === 0) return null;

  for (const area of areas) {
    const sub = area.subdivision;
    if (!sub) continue;
    const parts: string[] = [];
    if (sub.name) parts.push(sub.name);

    let parent = (sub as any).parent;
    while (parent) {
      if (parent.level <= 1 && parent.name) {
        parts.push(parent.name);
        break;
      }
      parent = parent.parent;
    }
    if (parts.length >= 2) return parts.join(", ");
  }

  const firstArea = areas[0]?.subdivision;
  if (firstArea?.name) return firstArea.name;
  return null;
}

export function AgentListItem({ agent }: AgentListItemProps) {
  const name = agent?.name ?? agent?.email ?? "";

  const { data: ratingData } = api.agents.getRating.useQuery(
    { agentId: agent.id },
    { retry: false, refetchOnWindowFocus: false },
  );

  const cityState = useMemo(() => extractCityState(agent), [agent]);

  const hasRating =
    ratingData && ratingData.totalReviews > 0 && ratingData.averageRating > 0;

  const listingCount = (agent as any)._count?.sellerAgentListings ?? null;
  const [years, setYears] = useState<number | null>(null);

  useEffect(() => {
    if (agent.profile?.agentExperienceStartDate != null) {
      setYears(differenceInYears(new Date(), new Date(agent.profile.agentExperienceStartDate)));
    }
  }, [agent.profile?.agentExperienceStartDate]);

  const yearsCount = years != null && years > 0 ? Math.max(1, years) : null;
  const organization =
    displayBrokerageName(agent.profile) ?? "Independent business broker";
  const specialties = useMemo(
    () =>
      (agent.agentIndustrySpecialties ?? [])
        .map((s) => s.industryCategory?.name)
        .filter((n): n is string => !!n),
    [agent.agentIndustrySpecialties],
  );

  return (
    <BrokerProfileCard
      href={buildAgentProfileUrl(agent) ?? "/business-brokers"}
      id={agent.id}
      name={name}
      image={agent.image}
      isVerified={agent.isVerified}
      organization={organization}
      locationLabel={cityState}
      specialties={specialties}
      bio={agent.profile?.bio}
      yearsCount={yearsCount}
      listingsCount={listingCount}
      ratingAverage={hasRating ? ratingData.averageRating : null}
      ratingTotal={hasRating ? ratingData.totalReviews : null}
      ctaLabel="View profile"
    />
  );
}

AgentListItem.displayName = "AgentListItem";
